/**
 * Start free: three documents, no card.
 *
 * The browser half of the same door the QGIS plugin opens. Outreach points
 * prospects at app.easting.ai and, until this existed, they arrived at a sign
 * in page telling them accounts come with a subscription
 * (`docs/plans/implemented/FREE_TIER.md`).
 *
 * Same-origin on purpose: the portal distribution proxies `/v1/*` to the API,
 * so this posts to a relative path and no CORS policy has to exist for it.
 * The key comes back in the response and is shown once, which is the same
 * promise the claim page has always made.
 *
 * Wears `SignIn`'s frame deliberately: the same `.signin` shell, wordmark and
 * footer, because these are two states of one first screen and the inputs are
 * only styled inside `.signin-card` anyway.
 */
import { useState } from "preact/hooks";

interface Props {
  onDone: () => void;
}

interface Granted {
  api_key: string;
  prefix: string;
  documents: number;
  /** False when the address already had an account, or identity was
   *  unavailable: either way no invitation went out, and the copy below must
   *  not claim one did. */
  invited: boolean;
}

export function Signup({ onDone }: Props) {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [granted, setGranted] = useState<Granted | null>(null);

  async function submit(event: Event) {
    event.preventDefault();
    setError("");
    if (!terms) {
      setError("Accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setBusy(true);
    try {
      const response = await fetch("/v1/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, company, role, accepted_terms: true }),
      });
      const body = await response.json();
      if (!response.ok) {
        // The server writes these for a person, so show what it said rather
        // than inventing a friendlier version that says less.
        setError(body?.error?.message ?? "Something went wrong. Try again.");
        return;
      }
      setGranted(body);
    } catch {
      setError("Could not reach Easting. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="signin">
      <div class="signin-column">
        {granted ? (
          <div class="signin-card">
            <a class="wordmark" href="https://easting.ai">
              easting<span>.ai</span>
            </a>
            <h1>Your key</h1>
            <p class="muted small">
              {granted.documents} documents, and they never expire. This is the
              only time the key is shown, so copy it now.
            </p>
            <p class="mono signup-key" data-testid="granted-key">
              {granted.api_key}
            </p>
            <p class="muted small">
              Paste it into the QGIS plugin under Easting settings, or send it
              as a bearer token against the API.{" "}
              {granted.invited ? (
                <>
                  A sign-in invitation is on its way to {email}, which is how
                  you get back in if the key is lost.
                </>
              ) : (
                <>
                  No invitation went out this time: if {email} already has an
                  account, sign in as usual. The key above is yours either way.
                </>
              )}
            </p>
            <button type="button" class="primary wide" onClick={onDone}>
              Go to sign in
            </button>
          </div>
        ) : (
          <form class="signin-card" onSubmit={submit}>
            <a class="wordmark" href="https://easting.ai">
              easting<span>.ai</span>
            </a>
            <h1>Start free</h1>
            <p class="muted small">
              Three documents on us, and no card. Extract a recorded deed or
              easement, verify closure and stated acreage, and place it on a
              map.
            </p>

            <label>
              Email
              <input
                type="email"
                autocomplete="username"
                required
                value={email}
                onInput={(event) =>
                  setEmail((event.target as HTMLInputElement).value)
                }
              />
            </label>
            <label>
              Company
              <input
                type="text"
                autocomplete="organization"
                required
                value={company}
                onInput={(event) =>
                  setCompany((event.target as HTMLInputElement).value)
                }
              />
            </label>
            <label>
              Role
              <input
                type="text"
                autocomplete="organization-title"
                required
                placeholder="Right of way agent, abstractor, GIS analyst"
                value={role}
                onInput={(event) =>
                  setRole((event.target as HTMLInputElement).value)
                }
              />
            </label>

            <label class="signup-terms">
              <input
                type="checkbox"
                checked={terms}
                onChange={(event) =>
                  setTerms((event.target as HTMLInputElement).checked)
                }
              />
              <span>
                I accept the{" "}
                <a href="https://easting.ai/terms/">Terms of Service</a> and{" "}
                <a href="https://easting.ai/privacy/">Privacy Policy</a>
              </span>
            </label>

            {error && <p class="error-text">{error}</p>}

            <button type="submit" class="primary wide" disabled={busy}>
              {busy ? "Starting…" : "Start free"}
            </button>

            <p class="muted small">
              Already have an account? <a href="/">Sign in</a>.
            </p>
          </form>
        )}
        <p class="signin-footer">
          <a href="https://docs.easting.ai/web/">Help</a>
          <a href="https://easting.ai/terms/">Terms</a>
          <a href="https://easting.ai/privacy/">Privacy</a>
          <a href="mailto:support@easting.ai">support@easting.ai</a>
        </p>
      </div>
    </div>
  );
}
